import type { ReactNode } from "react";
import { useCompetitorTracking } from "../../hooks/useCompetitorTracking";
import { useLanguage } from "../../i18n/language-provider";
import { calendarViewKeysFor, type CalendarViewKey } from "../../navigation/calendarViews";
import { calendarViewLabels } from "../drafts/calendarViewLabels";
import { AppLink } from "../navigation/AppLink";
import { RenderFaultBoundary } from "../RenderFaultBoundary";
import { cn } from "../ui/utils";
import { calendarViewHref } from "./prices-places";

/** `list`: the open rail's views popover. `grid`: chips in the folded flyout. `phone`: chips in the phone's menu, on rail tokens. */
export type CalendarViewLinksLayout = "list" | "grid" | "phone";

interface CalendarViewLinksProps {
    layout: CalendarViewLinksLayout;
    /** The heading over the views; in `list` it names the caller's popover by its id, elsewhere the list itself. */
    heading?: { id: string; text: string };
    /** The view the calendar draws, marked `aria-current="true"`; never `"page"`, which is the rail column's own. */
    shown: CalendarViewKey | null;
    connectionId: number | null | undefined;
    connectionVersion: number;
    /** Whether an unknown tracking switch may be read from the server. */
    read: boolean;
    /** Stands where the views cannot be drawn; reads nothing they were drawn from. */
    fallback: ReactNode;
    onOpenView: (view: CalendarViewKey) => void;
    /** Each view's address; by default its one-time request, the same from every page. */
    hrefFor?: (view: CalendarViewKey) => string;
}

interface LayoutClasses {
    block?: string;
    heading: string;
    list: string;
    item: string;
    idle: string;
    current: string;
}

const LAYOUTS: Record<CalendarViewLinksLayout, LayoutClasses> = {
    list: {
        heading: "px-2 pt-1 pb-1.5 text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground",
        list: "",
        item: "flex h-8 w-full min-w-0 items-center rounded-sm px-2 text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
        idle: "hover:bg-muted",
        current: "bg-accent text-primary font-medium",
    },
    grid: {
        block: "my-0.5 ml-4 border-l border-border pl-1.5",
        heading: "sr-only",
        list: "grid grid-cols-2 gap-0.5",
        item: "flex h-7 w-full min-w-0 items-center rounded-sm px-2 text-[0.8125rem] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
        idle: "text-muted-foreground hover:bg-muted hover:text-foreground",
        current: "bg-accent text-primary font-medium",
    },
    phone: {
        block: "mt-1 border-t border-sidebar-border pt-1",
        heading: "px-1.5 pb-1 pt-0.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-sidebar-foreground/50",
        list: "grid grid-cols-2 gap-1",
        item: "flex min-h-10 sm:min-h-9 min-w-0 items-center rounded-sm border px-2.5 text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring/50",
        idle: "border-sidebar-border text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        current: "border-transparent bg-accent text-primary font-medium",
    },
};

/**
 * The calendar views as real links, the market views only while the property tracks its competitors. The tracking
 * switch is server data, so the list stands inside a boundary of its own and a fault costs only the views.
 */
export function CalendarViewLinks({ connectionId, connectionVersion, fallback, ...props }: CalendarViewLinksProps) {
    return (
        <RenderFaultBoundary
            part="rail.calendarViews"
            retryKey={`${connectionId}:${connectionVersion}`}
            fallback={fallback}
        >
            <ViewList connectionId={connectionId} connectionVersion={connectionVersion} {...props} />
        </RenderFaultBoundary>
    );
}

function ViewList({
    layout,
    heading,
    shown,
    connectionId,
    connectionVersion,
    read,
    onOpenView,
    hrefFor = calendarViewHref,
}: Omit<CalendarViewLinksProps, "fallback">) {
    const { t } = useLanguage();
    const tracking = useCompetitorTracking(connectionId, connectionVersion, { read });
    const labels = calendarViewLabels(t);
    const classes = LAYOUTS[layout];
    const labelledBy = layout === "list" ? undefined : heading?.id;

    return (
        <div className={classes.block}>
            {heading && (
                <p id={heading.id} className={classes.heading}>
                    {heading.text}
                </p>
            )}
            <ul aria-labelledby={labelledBy} className={classes.list || undefined}>
                {calendarViewKeysFor(tracking).map((view) => {
                    const current = view === shown;
                    return (
                        <li key={view} className="min-w-0">
                            <AppLink
                                to={hrefFor(view)}
                                onNavigate={() => onOpenView(view)}
                                aria-current={current ? "true" : undefined}
                                data-rail-flyout-item=""
                                className={cn(classes.item, current ? classes.current : classes.idle)}
                            >
                                <span className="truncate">{labels[view]}</span>
                            </AppLink>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
